import { SvgPathBuilder } from '../svg/SvgPathBuilder';
import { PI_RADIANS_90, getAngularDistance } from '../utils/angles';
import {
  FLOATING_POINT_ERROR,
  isBetweenBounds,
  isCloseTo,
} from '../utils/numbers';
import { type Line, line } from './line';
import { POINT_SVG_MATCH_STR, type Point, point } from './point';
import { type NonZeroVector, type Vector, vector } from './vector';

export const SEGMENT_SVG_MATCH_STR = `M\\s*${POINT_SVG_MATCH_STR}\\s*L\\s*${POINT_SVG_MATCH_STR}`;
export const SEGMENT_SVG_MATCH_RE = new RegExp(`^${SEGMENT_SVG_MATCH_STR}$`);
type SegmentSvgRegExpMatch = [
  all: string,
  startX: string,
  startY: string,
  endX: string,
  endY: string,
];

/**
 * Implements line segment
 */
export interface Segment {
  readonly start: Point;
  readonly end: Point;
}

/**
 * Segment that is guaranteed to have start != end
 *
 * @see NonZeroVector
 */
declare const __nonzero: unique symbol;
export interface NonZeroSegment extends Segment {
  readonly [__nonzero]: never;
}

// factories

export function create(start: Point, end: Point): Segment {
  return { start, end };
}

export function createNonZero(start: Point, end: Point): NonZeroSegment {
  if (point.equals(start, end)) {
    throw new TypeError('Zero segment');
  }
  return { start, end } as NonZeroSegment;
}

export function fromSvgString(d: string): Segment {
  const match = d.trim().match(SEGMENT_SVG_MATCH_RE);
  if (!match) {
    throw new Error(`Unexpected SVG path: ${JSON.stringify(d)}`);
  }
  const [, startX, startY, endX, endY] = match as SegmentSvgRegExpMatch;
  return create(point.create(+startX, +startY), point.create(+endX, +endY));
}

// comparison

export function equals(a: Segment, b: Segment): boolean {
  return point.equals(a.start, b.start) && point.equals(a.end, b.end);
}

export function isNonZero(s: Segment): s is NonZeroSegment {
  return !point.equals(s.start, s.end);
}

// arithmetics

export function getLength(s: Segment): number {
  return point.getDistance(s.start, s.end);
}

export function getLengthX(s: Segment): number {
  return Math.max(s.start.x, s.end.x) - Math.min(s.start.x, s.end.x);
}

export function getLengthY(s: Segment): number {
  return Math.max(s.start.y, s.end.y) - Math.min(s.start.y, s.end.y);
}

// svg
export function getD(s: Segment, compact = false): string {
  return new SvgPathBuilder(compact).moveTo(s.start).lineTo(s.end).build();
}

// geometry

/**
 * Check if `point` that lies on the line given by the segment lies also on the segment
 *
 * Returns irrelevant results for points not on given line.
 */
export function containsLinePoint(
  s: Segment,
  p: Point,
  tolerance = FLOATING_POINT_ERROR,
): boolean {
  return (
    p.x <= Math.max(s.start.x, s.end.x) + tolerance &&
    p.x >= Math.min(s.start.x, s.end.x) - tolerance &&
    p.y <= Math.max(s.start.y, s.end.y) + tolerance &&
    p.y >= Math.min(s.start.y, s.end.y) - tolerance
  );
}

/**
 * Check if point lies exactly on the segment
 */
export function contains(
  s: Segment,
  p: Point,
  tolerance = FLOATING_POINT_ERROR,
): boolean {
  if (!isBetweenBounds(p.x, s.start.x, s.end.x, tolerance)) {
    return false;
  }
  // avoid division by zero
  if (s.end.x === s.start.x) {
    return (
      isCloseTo(p.x, s.end.x, tolerance) &&
      isBetweenBounds(p.y, s.start.y, s.end.y)
    );
  }
  if (s.end.y === s.start.y) {
    return (
      isCloseTo(p.y, s.end.y, tolerance) &&
      isBetweenBounds(p.x, s.start.x, s.end.x)
    );
  }
  return isCloseTo(
    (p.x - s.start.x) / (s.end.x - s.start.x),
    (p.y - s.start.y) / (s.end.y - s.start.y),
    tolerance,
  );
}

function getPointByRatio(s: Segment, r: number): Point {
  const r2 = 1 - r;
  return point.create(
    s.start.x * r2 + s.end.x * r,
    s.start.y * r2 + s.end.y * r,
  );
}

/**
 * @param s
 * @param r {number} - a number from 0 to 1 that represents location on the segment
 */
export function getPoint(s: Segment, r: number): Point {
  if (r > 1 || r < 0) {
    throw new RangeError();
  }
  return getPointByRatio(s, r);
}

export function move(s: Segment, direction: Vector, distance: number): Segment {
  return create(
    point.createOnVector(s.start, direction, distance),
    point.createOnVector(s.end, direction, distance),
  );
}

// destruction

export function toLine(s: NonZeroSegment): Line {
  const dx = s.start.x - s.end.x;
  if (dx === 0) {
    return { x: s.start.x };
  }
  const a = (s.start.y - s.end.y) / dx;
  return {
    a,
    b: s.end.y - a * s.end.x,
  };
}

/**
 * Vector always faces direction from `start` to `end` point
 */
export function toVector(s: NonZeroSegment): NonZeroVector;
export function toVector(s: Segment): Vector;
export function toVector(s: Segment): Vector {
  return vector.fromAtoB(s.start, s.end);
}

// makeGetSquaredDistance is also geometry, but must be declared after toLine since it is using it

/**
 * Makes a function that calculates squared distance from the segment.
 * Higher-order function is used to avoid recalculating line formula from the segment
 * on each run.
 */
export function makeGetSquaredDistance(s: Segment): (p: Point) => number {
  if (!isNonZero(s)) {
    return (p: Point) => point.getSquaredDistance(s.start, p);
  }

  const ln = toLine(s);
  return (p: Point): number => {
    const closest = line.findClosestPoint(ln, p);
    if (containsLinePoint(s, closest)) {
      return point.getSquaredDistance(p, closest);
    }
    return Math.min(
      point.getSquaredDistance(p, s.start),
      point.getSquaredDistance(p, s.end),
    );
  };
}

const findVectorIntersection = (s: Segment, v: Vector): Point[] => {
  if (!vector.isNonZero(v)) {
    return segment.contains(s, point.zero) ? [point.zero] : [];
  }
  const vectorLine = line.fromTwoPoints(point.zero, v);
  if (!segment.isNonZero(s)) {
    return line.containsPoint(vectorLine, s.start) ? [s.start] : [];
  }
  const intersection = line.intersectionPoint(vectorLine, toLine(s));
  if (intersection === null) {
    return [];
  }
  // we can consider intersection point as a vector from zero to its coordinates
  // to determine whether the intersection point lies on vector's or opposite direction
  const isOnVectorsDirection = vector.isNonZero(intersection)
    ? getAngularDistance(
        vector.getAngle(v),
        vector.getAngle(intersection),
        // we already know that point is on same or opposite direction,
        // so super-rough comparison is enough
      ) < PI_RADIANS_90
    : true;

  return segment.containsLinePoint(s, intersection) && isOnVectorsDirection
    ? [intersection]
    : [];
};

export const segment = {
  create,
  createNonZero,
  fromSvgString,

  equals,
  isNonZero,

  getLength,
  getLengthX,
  getLengthY,

  getD,

  getPoint,
  makeGetSquaredDistance,
  move,
  contains,
  containsLinePoint,
  findVectorIntersection,

  toLine,
  toVector,
};
